// useOrderAlerts.js
import { useEffect, useRef, useCallback, useState } from 'react';

const STATUS_SOUNDS = {
    new_order:   '/sounds/orderplaced.ogg',
    cancelled:   '/sounds/ordercancelled.ogg',
    cancelled_by_store: "/sounds/ordercancelledbystore.ogg",
    ready_order: '/sounds/orderready.ogg',
};

const MUTE_KEY = 'notifications_muted';

export function useOrderAlerts(updatedOrders, setUpdatedOrders) {
    const audioRef    = useRef(null);
    const intervalRef = useRef(null);

    const [muted, setMuted] = useState(
        () => localStorage.getItem(MUTE_KEY) === 'true'
    );

    const stopSound = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        clearInterval(intervalRef.current);
    }, []);

    const playSound = useCallback((status) => {
        stopSound();
        if (muted) return;
        const src = STATUS_SOUNDS[status];
        if (!src) return;
        const audio = new Audio(src);
        audioRef.current = audio;
        audio.play().catch(() => {});
        intervalRef.current = setInterval(() => {
            audio.currentTime = 0;
            audio.play().catch(() => {});
        }, 4000);
    }, [stopSound, muted]);

    // Play / stop when updatedOrders changes
    useEffect(() => {
        if (updatedOrders.length === 0) { stopSound(); return; }
        playSound(updatedOrders[0].status);
    }, [updatedOrders, playSound, stopSound]);

    // Cleanup on unmount
    useEffect(() => () => stopSound(), [stopSound]);

    const toggleMute = useCallback(() => {
        setMuted(prev => {
            const next = !prev;
            localStorage.setItem(MUTE_KEY, String(next));
            if (next) stopSound();
            return next;
        });
    }, [stopSound]);

    const dismissOrder = useCallback((id) => {
        setUpdatedOrders(prev => prev.filter(o => o.id !== id));
    }, [setUpdatedOrders]);

    return { dismissOrder, muted, toggleMute };
}